import * as React from "react";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  DND_DRAGGABLE_PROPS,
  DND_TOUCH_ACTIVATION,
  Icon,
  Kbd,
  KbdGroup,
  Label,
  Separator,
  sortableHandleProps,
  sortableStyle,
  Switch,
  Text,
  ROW_RUNGS,
  useAppShell,
} from "@salestrak/ui";

import { useModuleRail } from "./module-rail-context";
import { COMPACT_MODE_SHORTCUT } from "#app/lib/hooks/use-compact-mode-hotkey";
import { HOME_SECTIONS } from "#app/components/home/home-sections";
import type { Omittable } from "@salestrak/ui";
import { AnalyticsContext, NavigationEvent, trackEvent } from "#app/lib/analytics";

const trackCustomizeNav = (label: string, data: Record<string, unknown>) =>
  trackEvent({ context: AnalyticsContext.Navigation, event: NavigationEvent.CustomizeNav, label, data });

/**
 * CustomizeNavigationDialog — lets the user tailor the navigation:
 *
 *  - Navigation tab: toggle rail tiles on/off and drag them into a new order.
 *    Home is pinned: always enabled, always first, not draggable.
 *  - Home tab: drag the Home module menu's configurable sections into a new
 *    order (the fixed HomeNavSection is not listed).
 *  - Appearance: Compact mode switch — rail tiles render icon-only and the
 *    rail column narrows. Hidden on the compact tier, where the rail is the
 *    drawer's strip and the preference changes nothing.
 *
 * Every edit applies live: it reads/writes `ModuleRailContext`'s nav
 * preferences (optimistic local update + write-through to the server, like
 * `workflowOrder`). One Dialog serves both form factors — a full-height
 * sheet on a phone.
 */

export type CustomizeNavigationTab = "navigation" | "home";

// Reserved leading column so rows with and without a drag handle align.
const GRIP_COLUMN_WIDTH = 20;

// Uniform sortable-row height across both tabs.
const ROW_HEIGHT = ROW_RUNGS.control;

// Fixed body height so the dialog doesn't resize when switching tabs — the
// Appearance footer (Navigation tab only) eats into the tab area instead of
// extending the dialog.
const CONTENT_HEIGHT = 400;

/** Draggable row frame: grip handle + caller content, dnd-kit sortable. */
function SortableRow({
  id,
  label,
  children,
}: {
  id: string;
  /** The row's name, so its grip reads "Move <label>". */
  label: string;
  children: React.ReactNode;
}) {
  const {
    attributes: { role: _role, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({ id });

  return (
    <Box
      align="center"
      direction="row"
      gap="sm"
      minHeight={ROW_HEIGHT}
      ref={setNodeRef}
      style={sortableStyle(transform, { transition, isSorting, isDragging, dragOpacity: 0.3 })}
    >
      <Box
        width={GRIP_COLUMN_WIDTH}
        {...DND_DRAGGABLE_PROPS}
        {...sortableHandleProps(attributes, listeners)}
        role="button"
        aria-label={`Move ${label}`}
      >
        <Icon color="muted" name="grip-vertical" size="sm" />
      </Box>
      {children}
    </Box>
  );
}

/** Navigation tab — toggle + reorder the module rail tiles. Home is pinned. */
function NavigationTab() {
  // Renders straight from the shared nav preferences — edits land in the
  // rail instantly (optimistic) and write through to the server.
  const { railTiles, setRailTiles } = useModuleRail();
  const sortableTiles = railTiles.filter((tile) => tile.id !== "home");
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, { activationConstraint: DND_TOUCH_ACTIVATION }),
  );

  const handleToggleTile = (id: string, show: boolean) => {
    trackCustomizeNav("toggle_tile", { tile: id, show });
    setRailTiles(
      railTiles.map((tile) => (tile.id === id ? { ...tile, show } : tile)),
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const pinned = railTiles.filter((tile) => tile.id === "home");
    const rest = railTiles.filter((tile) => tile.id !== "home");
    const oldIndex = rest.findIndex((tile) => tile.id === active.id);
    const newIndex = rest.findIndex((tile) => tile.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    trackCustomizeNav("reorder_tiles", { tile: String(active.id) });
    setRailTiles([...pinned, ...arrayMove(rest, oldIndex, newIndex)]);
  };

  return (
    <Box direction="column" gap="2xs">
      {/* Home is pinned: always enabled and first — no drag handle, the
        * checkbox renders disabled. The empty leading Box keeps it aligned
        * with the draggable rows below. */}
      <Box align="center" direction="row" gap="sm" minHeight={ROW_HEIGHT}>
        <Box width={GRIP_COLUMN_WIDTH} />
        {/* `weight="normal"` cancels Label's font-medium so the checkbox rows
          * read exactly like the Home tab's plain section rows. */}
        <Label>
          <Checkbox checked disabled />
          <Text size="sm" weight="normal">
            Home
          </Text>
        </Label>
      </Box>
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <SortableContext
          items={sortableTiles.map((tile) => tile.id)}
          strategy={verticalListSortingStrategy}
        >
          {sortableTiles.map((tile, index) => (
            <SortableRow id={tile.id} key={`${tile.id}-${index}`} label={tile.label}>
              <Label>
                <Checkbox
                  checked={tile.show}
                  onCheckedChange={(checked) =>
                    handleToggleTile(tile.id, checked)
                  }
                />
                <Text size="sm" weight="normal">
                  {tile.label}
                </Text>
              </Label>
            </SortableRow>
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
}

/** Home tab — reorder the Home module menu's configurable sections. */
function HomeTab() {
  // Renders straight from the shared nav preferences — `HomeModuleMenu` picks
  // up the new order instantly (optimistic) and it writes through to the
  // server. Labels come from the shared section registry.
  const { homeSectionOrder, setHomeSectionOrder } = useModuleRail();
  const sections = homeSectionOrder.map((id) => ({
    id,
    label: HOME_SECTIONS.find((section) => section.id === id)?.label ?? id,
  }));
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, { activationConstraint: DND_TOUCH_ACTIVATION }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex((section) => section.id === active.id);
    const newIndex = sections.findIndex((section) => section.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    trackCustomizeNav("reorder_home_sections", { section: String(active.id) });
    setHomeSectionOrder(arrayMove([...homeSectionOrder], oldIndex, newIndex));
  };

  return (
    <Box direction="column" gap="2xs">
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <SortableContext
          items={sections.map((section) => section.id)}
          strategy={verticalListSortingStrategy}
        >
          {sections.map((section, index) => (
            <SortableRow id={section.id} key={`${section.id}-${index}`} label={section.label}>
              <Text size="sm">{section.label}</Text>
            </SortableRow>
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
}

/** Shared dialog body: tab strip, active tab, Appearance footer. */
function CustomizeNavigationContent({
  tab,
  onTabChange,
}: {
  tab: CustomizeNavigationTab;
  onTabChange: (tab: CustomizeNavigationTab) => void;
}) {
  const { compact, setCompact } = useModuleRail();
  const { tier } = useAppShell();
  const showAppearance = tab === "navigation" && tier !== "compact";

  return (
    // Fixed height: the flexed middle absorbs the tab switch (and the
    // Appearance footer's presence), so the dialog never changes size.
    <Box direction="column" gap="md" height={CONTENT_HEIGHT}>
      {/* Tab strip as buttons (matches the sandbox): the active tab is an
        * outline button with foreground text, the inactive a ghost button with
        * muted text — no segmented-control container or accent-coloured label. */}
      <Box align="center" direction="row" gap="xs">
        <Button
          onClick={() => onTabChange("navigation")}
          size="sm"
          variant={tab === "navigation" ? "outline" : "ghost"}
        >
          <Text
            size="sm"
            variant={tab === "navigation" ? undefined : "muted"}
            weight="medium"
          >
            Navigation
          </Text>
        </Button>
        <Button
          onClick={() => onTabChange("home")}
          size="sm"
          variant={tab === "home" ? "outline" : "ghost"}
        >
          <Text
            size="sm"
            variant={tab === "home" ? undefined : "muted"}
            weight="medium"
          >
            Home
          </Text>
        </Button>
      </Box>
      <Box flex={1}>
        {tab === "navigation" ? <NavigationTab /> : <HomeTab />}
      </Box>
      {showAppearance && (
        <>
          <Separator />
          <Box direction="column" gap="sm">
            <Text size="sm" weight="bold">
              Appearance
            </Text>
            <Box align="center" direction="row" justify="between">
              <Box align="center" direction="row" gap="sm">
                <Text size="sm" variant="muted">
                  Compact mode
                </Text>
                {/* The switch's keyboard twin — the global toggle the
                    ShortcutListener registers. */}
                <KbdGroup>
                  {COMPACT_MODE_SHORTCUT.map((key) => (
                    <Kbd key={key}>{key}</Kbd>
                  ))}
                </KbdGroup>
              </Box>
              <Switch
                aria-label="Compact mode"
                checked={compact}
                onCheckedChange={(next) => {
                  trackCustomizeNav("compact", { on: next, via: "dialog" });
                  setCompact(next);
                }}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

type CustomizeNavigationDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tab shown when the dialog opens. The rail's More tile opens
   * "navigation"; the Home module menu's footer button opens "home". */
  initialTab?: CustomizeNavigationTab;
}>;

export function CustomizeNavigationDialog({
  open,
  onOpenChange,
  initialTab = "navigation",
}: CustomizeNavigationDialogProps) {
  const [tab, setTab] = React.useState<CustomizeNavigationTab>(initialTab);

  // Re-seed the tab on every open so a selection from the previous open
  // doesn't leak into the next one.
  React.useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  return (
    <Dialog onOpenChange={onOpenChange} open={open} sheetSize="full">
      <DialogPopup size="md">
        <DialogHeader>
          <DialogTitle>Customize Navigation</DialogTitle>
        </DialogHeader>
        <DialogPanel>
          <CustomizeNavigationContent onTabChange={setTab} tab={tab} />
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}
